import type { State } from '../../Slice/index.js'
import type { ReportBlock } from '../../../Generated/ReportBlock.ts'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { VizSourceEntity } from '../../Slice/Visualization/VisualizationSliceTypes.js'
import { cubeSourceId, graphHandle, graphLabel, isCubeChartKind } from '../../Slice/Chat/chatViz.js'

/**
 * Free-form on-screen context surfaced to the model (`ChatRequest.vizContext`): the charts the user currently
 * has on the canvas, and — for IR-1 — the outline of the agent-authored report so the model can target existing
 * sections by id (UPDATE/REMOVE/REORDER). Returns undefined when there's nothing on screen.
 */
export function buildVizContext(state: typeof State): string | undefined {
  const parts = [buildGraphContext(state), buildReportOutline(state), buildGlobalContext(state)].filter(
    (p): p is string => !!p
  )
  return parts.length > 0 ? parts.join(' || ') : undefined
}

/**
 * The workspace-global CONTEXT (W-1): user-pinned facts that bridge conversation branches. Sent to the agent
 * every turn — regardless of which branch the active path is on — by appending it to the free-form `vizContext`
 * the model already reads (the same channel the report outline uses). Returns undefined when there are no facts.
 */
function buildGlobalContext(state: typeof State): string | undefined {
  const facts = (state.chat?.globalContext ?? []).map((f) => f.trim()).filter((f) => f !== '')
  if (facts.length === 0) return undefined
  return `Workspace context: ${facts.map((f) => `- ${f}`).join(' ')}`
}

/**
 * Summarise the charts the user currently has on the canvas — with each chart's ACTUAL current knob values,
 * not just the directive the agent last emitted (live-state readback fidelity). For a cube
 * chart the live workspace source is the source of truth (the user may have manually re-zoomed, and it holds
 * the resolved blast/scaling/level/spin/trait/sink knobs); we prefer it and fall back to the directive. For
 * Krona the live zoom comes from the chat slice (`krona-node`, posted by the embed). This lets the model
 * reason about — and updateChart from — what the user is really looking at. Returns undefined when empty.
 */
function buildGraphContext(state: typeof State): string | undefined {
  const openGraphs = state.chat?.openGraphs ?? []
  if (openGraphs.length === 0) return undefined

  const sources = state.visualization?.sources ?? {}
  const kronaSelections = state.chat?.kronaSelections ?? {}

  const parts = openGraphs.map((directive) => {
    const facts = isCubeChartKind(directive.chartKind)
      ? cubeFacts(directive, sources[cubeSourceId(directive)])
      : directive.chartKind === 'KRONA'
        ? kronaFacts(directive, kronaSelections[directive.scopeId])
        : tableFacts(directive)
    const suffix = facts.length > 0 ? ` — ${facts.join(', ')}` : ''
    return `[${graphHandle(directive)}] ${graphLabel(directive)}${suffix}`
  })

  return parts.join('; ')
}

/**
 * A compact outline of the agent-authored report (IR-1) — one entry per block with its stable `id`, `type` and
 * title — so the model can reference sections by id when issuing UPDATE/REMOVE/REORDER edits. Returns undefined
 * when the report is empty.
 */
function buildReportOutline(state: typeof State): string | undefined {
  const blocks = state.chat?.currentReport?.blocks ?? []
  if (blocks.length === 0) return undefined

  const entries = blocks.map((block: ReportBlock) => {
    const title = block.title ?? block.taxonEntry ?? (block.text ? block.text.slice(0, 40) : undefined)
    const titlePart = title ? ` "${title}"` : ''
    return `[id=${block.id ?? '?'} type=${block.type}${titlePart}]`
  })

  return `Current report sections: ${entries.join(', ')}`
}

/**
 * Live knob values for a cube chart, preferring the workspace source's resolved shared state (which reflects
 * any manual change the user made) over the directive echo. Covers blast source, scaling, count, taxon level,
 * grade, taxonomy zoom (selectedPath), trait overlay, SPIN, per-sink tuning, grouping and sample slice.
 */
function cubeFacts(d: VizDirective, src: VizSourceEntity | undefined): string[] {
  const shared = src?.shared
  const path = shared?.selectedPath && shared.selectedPath.length > 0 ? shared.selectedPath : d.selectedPath
  const trait = shared?.natureKPIs?.traitGroup ?? d.traitGroup
  const traitName = shared?.natureKPIs?.traitName ?? d.traitName
  const noTrait = shared?.natureKPIs?.noTraitMode ?? d.noTraitMode
  const spinAlgo = shared?.spin?.algorithmType ?? d.spinAlgorithm
  const spinAt = shared?.spin?.atTime ?? d.spinAtTime
  const grade = shared?.gradeThreshold ?? d.gradeThreshold
  const sinkParams = liveSinkParams(src) ?? d.sinkParams

  return compact([
    labelFor('source', shared?.blastType ?? d.blastSource),
    labelFor('level', shared?.taxonLevel ?? d.taxonLevel),
    labelFor('scaling', shared?.scalingMode ?? d.scalingMode),
    labelFor('count', shared?.countMode ?? d.countMode),
    grade != null ? `grade ≥ ${grade}` : undefined,
    d.groupByField ? `grouped by ${d.groupByField}` : undefined,
    d.sampleIds && d.sampleIds.length > 0 ? `${d.sampleIds.length} selected sample(s)` : undefined,
    path && path.length > 0 ? `zoomed into ${path.join(' > ')}` : undefined,
    traitName
      ? `filtered to trait '${traitName}' — showing its taxonomic breakdown`
      : trait
        ? `trait ${trait} (${noTrait ?? 'include'})`
        : undefined,
    spinAlgo ? `SPIN ${spinAlgo}${spinAt ? ` @ ${spinAt}` : ' (latest)'}` : undefined,
    sinkParams && Object.keys(sinkParams).length > 0 ? `sink ${JSON.stringify(sinkParams)}` : undefined
  ])
}

/** Krona has no workspace source; its live state is the directive's data knobs + the posted zoom (krona-node). */
function kronaFacts(d: VizDirective, kronaZoom: string[] | undefined): string[] {
  return compact([
    labelFor('source', d.blastSource),
    labelFor('scaling', d.scalingMode),
    labelFor('count', d.countMode),
    d.gradeThreshold != null ? `grade ≥ ${d.gradeThreshold}` : undefined,
    kronaZoom && kronaZoom.length > 0 ? `zoomed into ${kronaZoom.join(' > ')}` : undefined
  ])
}

/** The standalone OTU table seeds its state from the directive (no workspace source to read back). */
function tableFacts(d: VizDirective): string[] {
  return compact([
    labelFor('source', d.blastSource),
    d.groupMode ? `group ${d.groupMode}` : undefined,
    d.tableSort ? `sort ${d.tableSort}` : undefined,
    d.spinAlgorithm ? `SPIN ${d.spinAlgorithm}${d.spinAtTime ? ` @ ${d.spinAtTime}` : ' (latest)'}` : undefined,
    d.selectedPath && d.selectedPath.length > 0 ? `focused on ${d.selectedPath.join(' > ')}` : undefined
  ])
}

/** Params of the (single) sink attached to a chat cube source, if any. */
function liveSinkParams(src: VizSourceEntity | undefined): Record<string, unknown> | undefined {
  const sink = src ? Object.values(src.sinks)[0] : undefined
  return sink?.config?.params
}

function labelFor(name: string, value: unknown): string | undefined {
  return value ? `${name} ${value}` : undefined
}

function compact(facts: (string | undefined)[]): string[] {
  return facts.filter((f): f is string => !!f)
}
